import { useState } from 'react';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { Input } from '../../components/ui/Input';
import { useCreateImpactArticle } from '../../hooks/useContent';
import type { ImpactArticleResult } from '../../api/content';

export interface ImpactArticleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onStarted?: (result: ImpactArticleResult) => void;
}

type ImpactContentType = 'seo_article' | 'comparison_page';

/**
 * Starts an impact article from a title, a Peec AI link, or a competitor URL.
 *
 * There is deliberately one input rather than a mode switch — the server sniffs
 * what was pasted. The gates below are shown but not toggleable: an article
 * that skipped fact-checking isn't the same product, and making them optional
 * is how the pipeline would quietly decay back into what it replaced.
 */
export function ImpactArticleDialog({ isOpen, onClose, onStarted }: ImpactArticleDialogProps) {
  const [input, setInput] = useState('');
  const [contentType, setContentType] = useState<ImpactContentType>('seo_article');
  const [keywordSeed, setKeywordSeed] = useState('');
  const [keywordDatabase, setKeywordDatabase] = useState('');
  const [outlineDirective, setOutlineDirective] = useState('');
  const [result, setResult] = useState<ImpactArticleResult | null>(null);

  const create = useCreateImpactArticle();
  const error = create.error instanceof Error ? create.error.message : null;

  function handleClose() {
    setInput('');
    setKeywordSeed('');
    setKeywordDatabase('');
    setOutlineDirective('');
    setResult(null);
    create.reset();
    onClose();
  }

  async function handleStart() {
    const started = await create.mutateAsync({
      input: input.trim(),
      contentType,
      ...(keywordSeed.trim() ? { keywordSeed: keywordSeed.trim() } : {}),
      ...(keywordDatabase.trim() ? { keywordDatabase: keywordDatabase.trim().toLowerCase() } : {}),
      ...(outlineDirective.trim() ? { outlineDirective: outlineDirective.trim() } : {}),
    });
    setResult(started);
    onStarted?.(started);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New impact article"
      description="Researched from four sources, keyword-targeted, and fact-checked before it reaches your library."
      size="lg"
    >
      {result ? (
        <div className="space-y-4">
          <Alert variant="success">
            Started <strong>{result.topic}</strong>. You'll get a Slack DM when it's ready to review.
          </Alert>

          <div className="rounded-2xl border border-border p-3 space-y-2">
            <div className="text-body-sm font-medium text-text-primary">Keyword target</div>
            <div className="text-body-sm text-text-secondary">
              Targeting <strong>{result.keywordBrief.headTerm}</strong>
              {result.keywordBrief.headVolume
                ? ` — ${result.keywordBrief.headVolume.toLocaleString()} searches/month`
                : ''}
              {result.keywordBrief.headDifficulty !== null
                ? `, KD ${result.keywordBrief.headDifficulty}`
                : ''}
              .
            </div>
            {/* The head term often differs from what was typed — that is the point,
                so it is shown rather than silently substituted. */}
            {result.keywordBrief.headTerm.toLowerCase() !== result.topic.toLowerCase() && (
              <div className="text-body-xs text-text-tertiary">
                You asked for "{result.topic}" — the article will still be about that, but it leads with
                the higher-volume term above.
              </div>
            )}
            {result.keywordBrief.questionKeywords.length > 0 && (
              <div className="text-body-xs text-text-tertiary">
                Questions it will answer:{' '}
                {result.keywordBrief.questionKeywords.slice(0, 4).map((q) => q.keyword).join(', ')}
              </div>
            )}
          </div>

          {result.peecContext && result.peecContext.referenceUrls.length > 0 && (
            <div className="rounded-2xl border border-border p-3 space-y-1">
              <div className="text-body-sm font-medium text-text-primary">Competitor pages being read</div>
              {result.peecContext.referenceUrls.map((url) => (
                <div key={url} className="text-body-xs text-text-tertiary break-all">{url}</div>
              ))}
            </div>
          )}

          {result.keywordBrief.warnings.map((w) => (
            <Alert key={w} variant="warning">{w}</Alert>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-body-sm font-medium text-text-primary" htmlFor="impact-input">
              Title, Peec link, or competitor URL
            </label>
            <Input
              id="impact-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Best DDQ software  —  or  https://app.peec.ai/sources/urls/sr_…"
              autoFocus
            />
            <div className="text-body-xs text-text-tertiary">
              A Peec source link is resolved back to the page it measured, and that page is read as
              evidence alongside the live search results.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-body-sm font-medium text-text-primary" htmlFor="impact-seed">
                Keyword seed <span className="text-text-tertiary font-normal">(optional)</span>
              </label>
              <Input
                id="impact-seed"
                value={keywordSeed}
                onChange={(e) => setKeywordSeed(e.target.value)}
                placeholder="pqq tender"
              />
              <div className="text-body-xs text-text-tertiary">
                The phrase to research from, when the title above is a headline nobody types into
                Google. Leave empty to research the title itself.
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-body-sm font-medium text-text-primary" htmlFor="impact-database">
                Search market <span className="text-text-tertiary font-normal">(optional)</span>
              </label>
              <Input
                id="impact-database"
                value={keywordDatabase}
                onChange={(e) => setKeywordDatabase(e.target.value)}
                placeholder="us"
              />
              <div className="text-body-xs text-text-tertiary">
                Semrush database. Defaults to <code>us</code> — a UK-only subject needs{' '}
                <code>uk</code>, or its volumes come back near zero.
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-body-sm font-medium text-text-primary" htmlFor="impact-outline">
              Angle and required sections <span className="text-text-tertiary font-normal">(optional)</span>
            </label>
            <textarea
              id="impact-outline"
              value={outlineDirective}
              onChange={(e) => setOutlineDirective(e.target.value)}
              rows={5}
              placeholder="What angle to take, which sections must exist, which vendors to compare, anything the article must not say."
              className="w-full rounded-2xl border border-border bg-surface-primary px-3 py-2 text-body-sm text-text-primary placeholder:text-text-tertiary focus:border-border-strong focus:outline-none"
            />
            <div className="text-body-xs text-text-tertiary">
              Folded into the content brief alongside the keyword targets.
            </div>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-body-sm font-medium text-text-primary mb-1">Type</legend>
            {(
              [
                ['seo_article', 'Article', 'Ranked listicles, guides, and definitional pages.'],
                ['comparison_page', 'Comparison', 'Head-to-head or category comparison pages.'],
              ] as const
            ).map(([value, label, help]) => (
              <label
                key={value}
                className="flex gap-3 rounded-2xl border border-border p-3 cursor-pointer hover:border-border-strong"
              >
                <input
                  type="radio"
                  name="impact-type"
                  value={value}
                  checked={contentType === value}
                  onChange={() => setContentType(value)}
                  className="mt-1"
                />
                <div className="space-y-1">
                  <div className="text-body-sm font-medium text-text-primary">{label}</div>
                  <div className="text-body-xs text-text-tertiary">{help}</div>
                </div>
              </label>
            ))}
          </fieldset>

          <div className="rounded-2xl border border-border p-3 space-y-1">
            <div className="text-body-sm font-medium text-text-primary">Always applied</div>
            <ul className="text-body-xs text-text-tertiary space-y-0.5">
              <li>• Semrush keyword targeting — the article leads with the term people search</li>
              <li>• Perplexity deep research, Claude grounded search, and a Jina competitor scrape</li>
              <li>• Interlinking checked against the live site, every link verified</li>
              <li>• Claim-level fact checking — unsupported claims block the draft</li>
            </ul>
            <div className="text-body-xs text-text-tertiary pt-1">
              Takes several minutes. Nothing is written to Sanity — you push it yourself once you've read it.
            </div>
          </div>

          {error && <Alert variant="error">{error}</Alert>}
        </div>
      )}

      <ModalFooter>
        {result ? (
          <Button onClick={handleClose}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={create.isPending}>
              Cancel
            </Button>
            <Button onClick={handleStart} disabled={!input.trim() || create.isPending}>
              {create.isPending ? 'Starting…' : 'Start article'}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}
